import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'November 2, 2026';

export default function PrivacyPolicy() {
  useDocumentTitle('Privacy Policy');
  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        lineHeight: 1.7,
        color: '#1a1a1a',
        background: '#fafafa',
        padding: '2rem 1rem',
        minHeight: '100vh',
      }}
    >
      <div
        className="pl-legal"
        style={{
          maxWidth: 720,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 12,
          padding: 'clamp(1.25rem, 6vw, 3rem)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.25rem', color: '#111' }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '2rem' }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <p>
          Paywall Lab ("we", "us", or "our") operates as a third-party application on the Whop
          platform. This Privacy Policy describes what data we collect, how we use it, and your
          rights regarding that data.
        </p>

        <h2>1. What Paywall Lab Does</h2>
        <p>
          Paywall Lab is a price testing tool for Whop creators. A creator sets up a test with two
          or three prices for the same product, then shares it in one or more spots: a link they
          post (bio, Discord, YouTube and so on), links for partners and ads, or an Upgrade tab
          inside their whop. Each visitor sees one price, and we measure which price earns the
          most per visitor. Creators can also sell one-time access to an image through an{' '}
          <strong>unlock offer</strong>: a hosted page shows a blurred preview, and the buyer
          unlocks the original after paying through Whop's checkout on that page.
        </p>

        <h2>2. Data We Collect</h2>

        <p>
          <strong>About creators (you)</strong>
        </p>
        <ul>
          <li>Your Whop company ID, company name and store address (route)</li>
          <li>Your Whop user ID, as the creator of each test</li>
          <li>A cached list of your Whop products (IDs and names)</li>
          <li>When you accepted these terms, and your setup and review-prompt progress</li>
        </ul>

        <p>
          <strong>Your tests</strong>
        </p>
        <ul>
          <li>Test names, status and traffic split</li>
          <li>
            Each price: amount, currency, billing interval, trial days, initial fee, package label
            and promo code, plus the Whop plan and checkout IDs we create for it
          </li>
          <li>
            Each spot: its name, its short link code, and any source tag or Whop affiliate code you
            give it
          </li>
          <li>
            For unlock offers: the image you upload, the blurred preview we make from it, and the
            offer's title, description, prices and limits
          </li>
        </ul>

        <p>
          <strong>About visitors and buyers</strong>
        </p>
        <ul>
          <li>
            A <strong>visit</strong> each time someone opens a test: which test, which price they
            were shown, which spot they came through, and the traffic source if the link carried
            one (UTM tags, <code>?ref=</code>, or a Whop affiliate code)
          </li>
          <li>
            A <strong>visitor ID</strong>, so a returning visitor keeps seeing the same price and is
            counted once. It is, in order: the visitor's Whop user ID when Whop has verified who
            they are (for example, members opening the Upgrade tab); otherwise a random ID in a
            first-party cookie called <code>pl_vid</code> that we set on their first visit and that
            lasts a year. We never store raw IP addresses; visits from before November 2026 keep
            only a salted, one-way hash (HMAC) of the address
          </li>
          <li>
            Purchase events Whop sends us (payments, memberships, refunds and cancellations): Whop
            payment, membership, member and user IDs, amounts, and plan details
          </li>
          <li>
            On unlock offer pages: a purchase token kept in the buyer's browser so the page stays
            unlocked for them. If they sign in with Whop to restore a purchase, we also get their
            Whop user ID and set a sign-in cookie that expires within 7 days
          </li>
          <li>
            Older paid image links made before unlock offers, until they are moved over, keep a
            random visitor ID in the browser's storage and record the browser's user agent with
            each visit
          </li>
        </ul>
        <p>
          When a creator previews their own Upgrade tab, we don't record a visit.
        </p>

        <p>
          <strong>Billing</strong>
        </p>
        <ul>
          <li>Your Paywall Lab plan and its status</li>
          <li>The Whop membership and payment IDs of your Paywall Lab subscription</li>
          <li>Trial and billing period dates</li>
        </ul>

        <p>
          <strong>Analytics and pixels</strong>
        </p>
        <ul>
          <li>
            <strong>Google Analytics, on the creator dashboard only.</strong> When you use the
            Paywall Lab dashboard, Google Analytics records usage such as the pages you view and
            your browser and device, using Google's cookies. It is never loaded on pages that
            visitors, buyers or members see, or on this policy and our terms
          </li>
          <li>
            <strong>The Whop pixel, on hosted offer pages.</strong> Unlock offer pages load Whop's
            pixel, scoped to the creator's Whop account, and send it a page view so the creator
            can measure their Whop Ads. The checkout on that page is Whop's own. Whop handles this
            data under its own privacy policy
          </li>
        </ul>

        <h2>3. Data We Do NOT Collect</h2>
        <ul>
          <li>
            We do not collect buyer names, email addresses, phone numbers, or payment card details
          </li>
          <li>We do not store raw IP addresses, and we do not fingerprint browsers</li>
          <li>
            We do not set advertising cookies of our own. The only third-party scripts are the two
            described above: Google Analytics on the creator dashboard and the Whop pixel on hosted
            offer pages
          </li>
          <li>
            All payment processing is handled entirely by Whop — we never see or store payment
            credentials
          </li>
        </ul>

        <h2>4. How We Use Your Data</h2>
        <ul>
          <li>
            <strong>Running tests:</strong> To show each visitor one price, record visits and
            sales, and calculate results such as revenue per visitor and the chance each price
            beats the first one
          </li>
          <li>
            <strong>Consistent prices:</strong> The visitor ID above decides which price a visitor
            sees, so the same person keeps seeing the same price
          </li>
          <li>
            <strong>Unlock offers:</strong> To show buyers a preview, verify their purchase, and
            deliver the original image to them
          </li>
          <li>
            <strong>Improving the dashboard:</strong> Google Analytics tells us how creators use
            the dashboard
          </li>
          <li>
            <strong>Billing:</strong> To manage your Paywall Lab Pro subscription status
          </li>
        </ul>
        <p>
          We do not use your data for advertising, profiling, or any purpose unrelated to providing
          Paywall Lab's services.
        </p>
        <p>
          <strong>Legal basis (GDPR / UK GDPR).</strong> Where the GDPR or UK GDPR applies, we
          process your data on the basis of (a) our legitimate interests in providing, securing, and
          improving the Service, balanced against your privacy rights; (b) the performance of our
          contract with you under our Terms of Service; and (c) compliance with applicable legal
          obligations.
        </p>

        <h2>5. Who Else Handles Data</h2>
        <ul>
          <li>
            <strong>Whop.</strong> Whop sends us webhooks when payments, memberships, refunds, or
            cancellations happen. We call Whop's API to create and manage plans and checkouts on
            your behalf. When a buyer signs in with Whop to restore a purchase, Whop tells us their
            Whop user ID. The Whop pixel and the checkout on offer pages are Whop's
          </li>
          <li>
            <strong>Cloudflare R2</strong> stores unlock offer images and previews, only to store
            and serve them on our behalf
          </li>
          <li>
            <strong>Google Analytics</strong> receives creator dashboard usage, as described in
            section 2
          </li>
        </ul>
        <p>
          We do not sell or rent your data, and we do not share it with advertising networks or
          data brokers. Specifically, we do not:
        </p>
        <ul>
          <li>Use your data for competitive benchmarking against other Paywall Lab customers</li>
          <li>Transfer your data to any other application or service we operate</li>
          <li>Include your data in any search engine, directory, or other public listing</li>
          <li>Contact your visitors, buyers, or members directly</li>
        </ul>

        <h2>6. Data Retention and Storage</h2>
        <ul>
          <li>
            <strong>Test and results data:</strong> Retained for as long as your account is active
          </li>
          <li>
            <strong>Webhook processing records:</strong> Automatically deleted after 30 days
          </li>
          <li>
            <strong>Cookies:</strong> The <code>pl_vid</code> visitor cookie lasts a year. The
            restore-purchase sign-in cookie expires within 7 days
          </li>
          <li>
            <strong>Deletion requests:</strong> You may request deletion of your data at any time,
            including after uninstalling the app, by contacting us through the Whop platform. We
            honor verifiable deletion requests within 30 days, in accordance with Whop's Developer
            Terms
          </li>
        </ul>
        <p>
          Residual copies of data may persist in routine system backups after deletion and will be
          overwritten on a rolling schedule within a reasonable period.
        </p>
        <p>
          <strong>Data storage and international transfers.</strong> Your data is stored and
          processed on servers located in the United States. If you are located outside the United
          States, including in the European Economic Area, United Kingdom, or Switzerland, your data
          may be transferred to and processed in jurisdictions that may not provide the same level
          of data protection as your home country. Where required by applicable law, we rely on
          appropriate safeguards for such transfers, including the European Commission's Standard
          Contractual Clauses.
        </p>

        <h2>7. Data Security</h2>
        <p>We implement reasonable security measures to protect your data, including:</p>
        <ul>
          <li>HTTPS encryption for all data in transit</li>
          <li>Webhook signature verification to ensure data integrity</li>
          <li>Rate limiting to prevent abuse</li>
          <li>Authentication via Whop's token verification for all API access</li>
        </ul>
        <p>
          In the event of a data breach involving unauthorized access to your data, we will notify
          Whop within 24 hours and take immediate steps to remedy the breach, as required by Whop's
          Developer Terms.
        </p>

        <h2>8. Your Rights</h2>
        <p>
          <strong>For all users:</strong>
        </p>
        <ul>
          <li>Request information about what data we hold about your company</li>
          <li>
            Request deletion of your data at any time by contacting us or uninstalling the app
          </li>
          <li>Stop data collection by pausing or ending your tests</li>
        </ul>

        <p>
          <strong>
            Additional rights for users in the European Economic Area, United Kingdom, and
            Switzerland (GDPR / UK GDPR):
          </strong>
        </p>
        <ul>
          <li>
            <strong>Access:</strong> Request a copy of the personal data we hold about you
          </li>
          <li>
            <strong>Rectification:</strong> Request correction of inaccurate or incomplete data
          </li>
          <li>
            <strong>Erasure:</strong> Request deletion of your data, subject to limited legal
            exceptions
          </li>
          <li>
            <strong>Restriction:</strong> Request that we limit how we process your data
          </li>
          <li>
            <strong>Portability:</strong> Request a copy of your data in a structured, commonly
            used, machine-readable format
          </li>
          <li>
            <strong>Objection:</strong> Object to processing based on our legitimate interests
          </li>
          <li>
            <strong>Withdraw consent:</strong> Where processing is based on consent, withdraw it at
            any time without affecting the lawfulness of prior processing
          </li>
          <li>
            <strong>Lodge a complaint:</strong> File a complaint with your local data protection
            supervisory authority
          </li>
        </ul>

        <p>
          <strong>Additional rights for California residents (CCPA / CPRA):</strong>
        </p>
        <ul>
          <li>
            <strong>Right to know:</strong> Request disclosure of the categories and specific pieces
            of personal information we have collected, the sources of that information, and the
            purposes for which it is used
          </li>
          <li>
            <strong>Right to delete:</strong> Request deletion of your personal information, subject
            to limited legal exceptions
          </li>
          <li>
            <strong>Right to correct:</strong> Request correction of inaccurate personal information
          </li>
          <li>
            <strong>Right to opt out of sale or sharing:</strong> We do not sell personal
            information, and we do not share it for cross-context behavioral advertising ourselves.
            The Whop pixel on hosted offer pages is Whop's and is covered by Whop's privacy policy
          </li>
          <li>
            <strong>Right to limit use of sensitive personal information:</strong> We do not use or
            disclose sensitive personal information for purposes beyond providing the Service
          </li>
          <li>
            <strong>Non-discrimination:</strong> We will not discriminate against you for exercising
            any of these rights
          </li>
        </ul>

        <p>
          To exercise any of these rights, contact us through the Whop platform via the Paywall Lab
          app listing. We will respond within the timeframes required by applicable law (typically
          within thirty (30) days under GDPR and forty-five (45) days under the CCPA, with
          extensions where permitted by law). We may need to verify your identity before responding
          to your request.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          Paywall Lab is not directed at individuals under the age of 18. We do not knowingly
          collect data from children.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Continued use of Paywall Lab after
          changes constitutes acceptance of the updated policy.
        </p>

        <h2>11. Contact</h2>
        <p>
          If you have questions about this Privacy Policy or our data practices, please contact us
          through the Whop platform via the Paywall Lab app listing.
        </p>

        <div
          style={{
            marginTop: '2.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #eee',
            fontSize: '0.85rem',
            color: '#888',
          }}
        >
          <p>&copy; {new Date().getFullYear()} Paywall Lab. All rights reserved.</p>
          <p>
            <Link
              to="/terms"
              className="pl-tap"
              style={{ color: '#2563eb', textDecoration: 'none' }}
            >
              Terms of Service
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
